import { supabase } from './supabase';
import {
  TEAMS,
  STICKERS_PER_TEAM,
  TOTAL_STICKERS,
  COCA_COLA_COUNT,
  getTeamStickerCodes,
} from '../data/stickers';
import {
  TEAMS_BY_CONFEDERATION,
  ACHIEVEMENT_ID_BY_CONFEDERATION,
  type Confederation,
} from '../data/teamConfederations';

interface CheckContext {
  counts: Record<string, number>;
  friendCount: number;
  confirmedTrades: number;
  longestStreakDays: number;
  registeredAt?: string;
}

const LAUNCH_ISO = '2026-04-28'; // janela "Madrugador" — 1ª semana de lançamento

function teamComplete(counts: Record<string, number>, teamCode: string): boolean {
  const codes = getTeamStickerCodes(teamCode);
  return codes.every((c) => (counts[c] || 0) >= 1);
}

function confederationComplete(counts: Record<string, number>, confed: Confederation): boolean {
  return TEAMS_BY_CONFEDERATION[confed].every((teamCode) => teamComplete(counts, teamCode));
}

function ownedTotal(counts: Record<string, number>): number {
  let owned = 0;
  for (const team of TEAMS) {
    for (let i = 1; i <= STICKERS_PER_TEAM; i++) {
      if ((counts[`${team.code}${i}`] || 0) >= 1) owned++;
    }
  }
  // FWC entram na contagem
  for (let i = 0; i <= 19; i++) {
    if ((counts[`FWC${i}`] || 0) >= 1) owned++;
  }
  return owned;
}

function cocaComplete(counts: Record<string, number>): boolean {
  for (let i = 1; i <= COCA_COLA_COUNT; i++) {
    if ((counts[`CC${i}`] || 0) === 0) return false;
  }
  return true;
}

// Lista de (achievement_id, predicate). Avaliada em ordem.
const PREDICATES: Array<{ id: string; check: (ctx: CheckContext) => boolean }> = [
  { id: 'first10', check: (ctx) => ownedTotal(ctx.counts) >= 10 },
  { id: 'first100', check: (ctx) => ownedTotal(ctx.counts) >= 100 },
  { id: 'half', check: (ctx) => ownedTotal(ctx.counts) >= TOTAL_STICKERS * 0.5 },
  { id: 'full', check: (ctx) => ownedTotal(ctx.counts) >= TOTAL_STICKERS },
  { id: 'conmebol', check: (ctx) => confederationComplete(ctx.counts, 'CONMEBOL') },
  { id: 'uefa', check: (ctx) => confederationComplete(ctx.counts, 'UEFA') },
  { id: 'concacaf', check: (ctx) => confederationComplete(ctx.counts, 'CONCACAF') },
  { id: 'caf', check: (ctx) => confederationComplete(ctx.counts, 'CAF') },
  { id: 'afc', check: (ctx) => confederationComplete(ctx.counts, 'AFC') },
  { id: 'ofc', check: (ctx) => confederationComplete(ctx.counts, 'OFC') },
  { id: 'firstFriend', check: (ctx) => ctx.friendCount >= 1 },
  { id: 'tenTrades', check: (ctx) => ctx.confirmedTrades >= 10 },
  { id: 'fiftyTrades', check: (ctx) => ctx.confirmedTrades >= 50 },
  { id: 'network10', check: (ctx) => ctx.friendCount >= 10 },
  { id: 'streak7', check: (ctx) => ctx.longestStreakDays >= 7 },
  { id: 'streak30', check: (ctx) => ctx.longestStreakDays >= 30 },
  { id: 'cocaSet', check: (ctx) => cocaComplete(ctx.counts) },
  {
    id: 'earlyBird',
    check: (ctx) => {
      if (!ctx.registeredAt) return false;
      const launch = new Date(LAUNCH_ISO).getTime();
      const week = 7 * 24 * 60 * 60 * 1000;
      const t = new Date(ctx.registeredAt).getTime();
      return t >= launch && t < launch + week;
    },
  },
];

async function loadContext(userId: string): Promise<CheckContext | null> {
  const [{ data: stickers }, { count: friendCount }, { count: tradesCount }, { data: activity }, { data: userRow }] =
    await Promise.all([
      supabase.from('stickers').select('sticker_code, count').eq('user_id', userId),
      supabase
        .from('friendships')
        .select('friend_id', { count: 'exact', head: true })
        .eq('user_id', userId),
      supabase
        .from('trades')
        .select('id', { count: 'exact', head: true })
        .or(`user_a.eq.${userId},user_b.eq.${userId}`)
        .eq('status', 'confirmed'),
      supabase
        .from('daily_activity')
        .select('date')
        .eq('user_id', userId)
        .order('date', { ascending: false })
        .limit(120),
      supabase.auth.getUser(),
    ]);

  if (!stickers) return null;

  const counts: Record<string, number> = {};
  for (const row of stickers as Array<{ sticker_code: string; count: number }>) {
    counts[row.sticker_code] = row.count;
  }

  return {
    counts,
    friendCount: friendCount ?? 0,
    confirmedTrades: tradesCount ?? 0,
    longestStreakDays: longestStreak((activity ?? []).map((r: { date: string }) => r.date)),
    registeredAt: userRow?.user?.created_at,
  };
}

export function longestStreak(dates: string[]): number {
  if (dates.length === 0) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const set = new Set(dates);
  // Streak terminando hoje OU ontem
  let cursor = new Date(today);
  if (!set.has(toISODate(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
    if (!set.has(toISODate(cursor))) return 0;
  }
  let streak = 0;
  while (set.has(toISODate(cursor))) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Verifica todas as conquistas para o usuário e insere as novas.
 * Retorna a lista de IDs de conquistas recém-desbloqueadas (pra toast).
 * Falha silenciosa em qualquer erro (background task).
 */
export async function checkAchievements(userId: string): Promise<string[]> {
  try {
    const ctx = await loadContext(userId);
    if (!ctx) return [];

    const earnedIds: string[] = [];
    for (const pred of PREDICATES) {
      if (pred.check(ctx)) earnedIds.push(pred.id);
    }
    if (earnedIds.length === 0) return [];

    // Busca quais já tem
    const { data: already } = await supabase
      .from('user_achievements')
      .select('achievement_id')
      .eq('user_id', userId)
      .in('achievement_id', earnedIds);

    const alreadySet = new Set((already ?? []).map((r: { achievement_id: string }) => r.achievement_id));
    const newIds = earnedIds.filter((id) => !alreadySet.has(id));
    if (newIds.length === 0) return [];

    // Insere as novas (idempotente via PK; on conflict do nothing)
    const rows = newIds.map((id) => ({ user_id: userId, achievement_id: id }));
    const { error: insErr } = await supabase
      .from('user_achievements')
      .insert(rows);

    if (insErr) {
      console.error('checkAchievements insert error', insErr);
      return [];
    }

    // Gera feed_event achievement_earned pra cada uma (visível pros amigos)
    const feedRows = newIds.map((id) => ({
      user_id: userId,
      event_type: 'achievement_earned',
      payload: { achievement_id: id },
      event_key: `achievement_earned:${id}`,
    }));
    await supabase
      .from('feed_events')
      .upsert(feedRows, { onConflict: 'user_id,event_key', ignoreDuplicates: true });

    return newIds;
  } catch (err) {
    console.error('checkAchievements failed', err);
    return [];
  }
}

/**
 * Mapeia ACHIEVEMENT_ID_BY_CONFEDERATION pra usar fora se preciso.
 */
export { ACHIEVEMENT_ID_BY_CONFEDERATION };
